import { useQuery } from '@tanstack/react-query'
import { ShieldOff, Smartphone } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import type { UserDetail } from '@/api/admin-types'
import { branchListQueryOptions } from '@/api/reference-api'
import type { Branch } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { StatusBadge } from '@/components/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { branchLabel } from '@/lib/branch'
import { formatDateTime } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import { MINIMUM_REASON_LENGTH } from './constants'
import { useRevokeDevice } from './mutations'
import { useAdminPermissions } from './permissions'

/**
 * The panels behind a user's record.
 *
 * One rule from the customer and loan panels carries over unchanged: a value the server did not
 * send is not rendered at all, while a value it sent as null or empty is a dash. Nothing here is
 * ever left blank in a way that could be read as "the server said nothing" when the truth is "the
 * server said no".
 */

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === ''

  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm break-words text-heading', mono && 'font-mono')}>
        {empty ? <span className="text-muted">—</span> : value}
      </dd>
    </div>
  )
}

function resolveBranchLabel(branches: Branch[], branchId: string): string {
  const match = branches.find((branch) => branch.id === branchId)
  return match ? branchLabel(match) : `Branch ${branchId}`
}

export function ProfilePanel({ user }: { user: UserDetail }) {
  const branches = useQuery(branchListQueryOptions())

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Employee code" value={user.employeeCode} mono />
          <Field label="Email" value={user.email} />
          <Field label="Phone" value={user.phoneNumber} mono />
          <Field
            label="Branch"
            value={resolveBranchLabel(branches.data ?? [], user.branchId)}
          />
          <Field
            label="Loan officer"
            value={
              <Badge tone={user.isLoanOfficer ? 'brand' : 'neutral'}>
                {user.isLoanOfficer ? 'Yes' : 'No'}
              </Badge>
            }
          />
          <Field
            label="Teller"
            value={<Badge tone={user.isTeller ? 'brand' : 'neutral'}>{user.isTeller ? 'Yes' : 'No'}</Badge>}
          />
          <Field label="Created" value={formatDateTime(user.createdAtUtc)} />
          <Field
            label="Last sign-in"
            value={user.lastLoginUtc ? formatDateTime(user.lastLoginUtc) : 'Never'}
          />
          <Field
            label="Failed sign-in attempts"
            value={user.failedAccessAttempts > 0 ? String(user.failedAccessAttempts) : null}
          />
        </dl>
      </CardContent>
    </Card>
  )
}

export function AccessPanel({ user }: { user: UserDetail }) {
  const branches = useQuery(branchListQueryOptions())

  return (
    <Card>
      <CardHeader>
        <CardTitle>Roles and branch access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Roles</p>
          {user.roles.length === 0 ? (
            <p className="mt-1 text-sm text-muted">No role is assigned. This account can sign in and do nothing else.</p>
          ) : (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {user.roles.map((role) => (
                <Badge key={role} tone="brand">
                  {role}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-2xs font-semibold tracking-wide text-muted uppercase">
            Additional branch access
          </p>
          {user.additionalBranches.length === 0 ? (
            <p className="mt-1 text-sm text-muted">
              None beyond the home branch above.
            </p>
          ) : (
            <ul className="mt-1.5 space-y-1">
              {user.additionalBranches.map((grant) => (
                <li key={grant.branchId} className="flex items-center gap-2 text-sm text-body">
                  <span>{resolveBranchLabel(branches.data ?? [], grant.branchId)}</span>
                  {grant.expiresAtUtc ? (
                    <span className="text-xs text-muted">
                      expires {formatDateTime(grant.expiresAtUtc)}
                    </span>
                  ) : (
                    <span className="text-xs text-muted">no expiry</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function SecurityPanel({ user }: { user: UserDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Security</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field
            label="Two-factor"
            value={
              <StatusBadge status={user.mfaEnrolled ? 'Active' : 'Disabled'} tone={user.mfaEnrolled ? 'success' : 'warning'} />
            }
          />
          <Field
            label="Enrolled"
            value={user.mfaEnrolledAtUtc ? formatDateTime(user.mfaEnrolledAtUtc) : null}
          />
          <Field
            label="Recovery codes remaining"
            value={user.mfaEnrolled ? String(user.remainingRecoveryCodes) : null}
          />
          <Field
            label="Must change password"
            value={<Badge tone={user.mustChangePassword ? 'warning' : 'neutral'}>{user.mustChangePassword ? 'Yes' : 'No'}</Badge>}
          />
          <Field
            label="Locked out"
            value={<Badge tone={user.lockedOut ? 'danger' : 'neutral'}>{user.lockedOut ? 'Yes' : 'No'}</Badge>}
          />
          <Field
            label="Lockout ends"
            value={user.lockoutEndsAtUtc ? formatDateTime(user.lockoutEndsAtUtc) : null}
          />
        </dl>
      </CardContent>
    </Card>
  )
}

/**
 * A user's registered field devices, and revoking one.
 *
 * Platform and status render as raw numbers rather than a name: the OpenAPI document carries no
 * enum metadata for either, and nothing else in this codebase says what the integers mean — see
 * `admin-types.ts`. Revoking needs only the device's own id, so it works whether or not the
 * platform or status could be read.
 */
export function DevicesPanel({ user }: { user: UserDetail }) {
  const permissions = useAdminPermissions()
  const [revokingDeviceId, setRevokingDeviceId] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const revoke = useRevokeDevice(user.userId)

  const trimmed = reason.trim()
  const ready = trimmed.length >= MINIMUM_REASON_LENGTH

  const close = () => {
    setRevokingDeviceId(null)
    setReason('')
    revoke.reset()
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Field devices</CardTitle>
      </CardHeader>
      <CardContent>
        {user.devices.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted">
            <Smartphone className="size-4" aria-hidden />
            No device is registered to this account.
          </p>
        ) : (
          <ul className="space-y-2">
            {user.devices.map((device) => (
              <li
                key={device.deviceId}
                className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-border-subtle p-2.5"
              >
                <div className="min-w-0">
                  <p className="font-mono text-xs text-heading">{device.deviceUid}</p>
                  <p className="text-2xs text-muted">
                    Registered {formatDateTime(device.registeredAtUtc)} · platform {device.platform} ·
                    status {device.status}
                    {device.lastSyncUtc ? <> · last synced {formatDateTime(device.lastSyncUtc)}</> : null}
                  </p>
                </div>
                {permissions.canRevokeDevice ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setRevokingDeviceId(device.deviceId)
                    }}
                  >
                    <ShieldOff />
                    Revoke
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={revokingDeviceId !== null} onOpenChange={(open) => (!open ? close() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke this device</DialogTitle>
            <DialogDescription>
              Its session ends and the next sync forces a local wipe. Re-registering needs the device
              enrolled again from the field app.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="revoke-device-reason">Reason</Label>
              <Input
                id="revoke-device-reason"
                value={reason}
                autoFocus
                autoComplete="off"
                onChange={(event) => setReason(event.target.value)}
                aria-describedby="revoke-device-reason-help"
              />
              <p id="revoke-device-reason-help" className="text-xs text-muted">
                Lost, stolen, or a staff member has left. Written into the audit trail.
              </p>
            </div>

            {revoke.isError ? <ProblemAlert error={revoke.error} /> : null}
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={close} disabled={revoke.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!ready || revoke.isPending}
              onClick={() => {
                if (!revokingDeviceId || !ready) {
                  return
                }
                revoke.mutate(
                  { deviceId: revokingDeviceId, reason: trimmed },
                  { onSuccess: close },
                )
              }}
            >
              {revoke.isPending ? 'Working…' : 'Revoke device'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
